import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  watch,
  onMounted,
  provide,
  reactive,
  toRefs,
} from 'vue'
import type { Tab, TabsState } from "./types"
import {
  AddTabKey,
  UpdateTabKey,
  DeleteTabKey,
  TabsProviderKey,
} from './symbols'
import AppTab from './AppTab.vue'

type ClassPropShape =
  | string
  | Record<string, boolean>
  | Array<string | Record<string, boolean>>

type Props = {
  id?: string
  options?: {
    useUrlFragment?: boolean
    defaultTabHash?: string
  }
  wrapperClass?: ClassPropShape
  panelsWrapperClass?: ClassPropShape
  navClass?: ClassPropShape
  navItemClass?: ClassPropShape
  navItemDisabledClass?: ClassPropShape
  navItemActiveClass?: ClassPropShape
  navItemInactiveClass?: ClassPropShape
  navItemLinkClass?: ClassPropShape
  navItemLinkActiveClass?: ClassPropShape
  navItemLinkInactiveClass?: ClassPropShape
  navItemLinkDisabledClass?: ClassPropShape
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppTabs',
  props: {
    id: { default: undefined },
    options: { default: () => ({
    useUrlFragment: true,
    defaultTabHash: undefined,
  }) },
    wrapperClass: { default: 'tabs-component' },
    panelsWrapperClass: { default: 'tabs-component__panels' },
    navClass: { default: 'tabs-component__tabs' },
    navItemClass: { default: 'tabs-component__tab' },
    navItemDisabledClass: { default: 'is-disabled' },
    navItemActiveClass: { default: 'is-active' },
    navItemInactiveClass: { default: 'is-inactive' },
    navItemLinkClass: { default: 'tabs-component__button' },
    navItemLinkActiveClass: { default: 'is-active' },
    navItemLinkInactiveClass: { default: 'is-inactive' },
    navItemLinkDisabledClass: { default: 'is-disabled' }
  },
  emits: ['changed', 'clicked'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const state: TabsState = reactive({
  activeTabHash: '',
  lastActiveTabHash: '',
  tabs: [],
})

provide(TabsProviderKey, state)

provide(AddTabKey, (tab) => {
  state.tabs.push(tab)
})

provide(UpdateTabKey, (computedId: string, data: Tab) => {
  const tabIndex = state.tabs.findIndex((tab) => tab.computedId === computedId)

  data.isActive = state.tabs[tabIndex].isActive

  state.tabs[tabIndex] = data
})

provide(DeleteTabKey, (computedId) => {
  const tabIndex = state.tabs.findIndex((tab) => tab.computedId === computedId)

  state.tabs.splice(tabIndex, 1)
})


const selectTab = (selectedTabHash: string, event?: Event): void => {
  const selectedTab = findTab(selectedTabHash)

  if (!selectedTab) {
    return
  }

  if (event && selectedTab.isDisabled) {
    event.preventDefault()
    return
  }

  if (state.lastActiveTabHash === selectedTab.hash) {
    emit('clicked', { tab: selectedTab })
    return
  }

  state.tabs.forEach((tab) => {
    tab.isActive = tab.hash === selectedTab.hash
  })

  emit('changed', { tab: selectedTab })

  state.lastActiveTabHash = state.activeTabHash = selectedTab.hash

}

const findTab = (hash: string) => {
  return state.tabs.find((tab) => tab.hash === hash)
}

watch(() => state.tabs.length, (newVal) => {
  selectTab(state.tabs[0].hash)
})

onMounted(() => {
  if (!state.tabs.length) {
    return
  }

  selectTab(state.tabs[0].hash)
})

__expose({
  ...toRefs(state),
  selectTab,
  findTab,
})

const __returned__ = { props, emit, state, selectTab, findTab, computed, watch, onMounted, provide, reactive, toRefs, get AddTabKey() { return AddTabKey }, get UpdateTabKey() { return UpdateTabKey }, get DeleteTabKey() { return DeleteTabKey }, get TabsProviderKey() { return TabsProviderKey }, AppTab }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})